import React from 'react';
import { Card, CardContent, Box, Typography, Avatar, CircularProgress } from '@mui/material';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import CountUp from 'react-countup';

interface EnhancedStatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  trend?: string;
  sparklineData?: number[];
  isPercentage?: boolean;
  percentageValue?: number;
}

const EnhancedStatCard: React.FC<EnhancedStatCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  sparklineData,
  isPercentage,
  percentageValue,
}) => {
  // Parsear el valor numérico para CountUp
  const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[^\d.-]/g, '')) : value;
  const isValidNumber = !isNaN(numericValue);
  const suffix = typeof value === 'string' && value.includes('%') ? '%' : '';

  return (
    <Card
      sx={{
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        minHeight: 220,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: 6,
          transform: 'translateY(-4px)',
          transition: 'all 0.3s ease-in-out',
        },
        transition: 'all 0.3s ease-in-out',
      }}
    >
      {/* Gradiente de fondo sutil */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: '150px',
          height: '150px',
          background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      <CardContent sx={{ position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flex={1}>
          {/* Contenido principal */}
          <Box flex={1}>
            <Typography color="textSecondary" gutterBottom variant="body2" fontWeight={500}>
              {title}
            </Typography>
            
            {/* Valor animado o con CircularProgress */}
            <Box display="flex" alignItems="center" gap={2}>
              {isValidNumber ? (
                <Typography variant="h4" component="div" fontWeight="bold" color="text.primary">
                  <CountUp
                    end={numericValue}
                    duration={1.5}
                    decimals={suffix === '%' ? 1 : 0}
                    suffix={suffix}
                    separator=","
                  />
                </Typography>
              ) : (
                <Typography variant="h4" component="div" fontWeight="bold" color="text.primary">
                  {value}
                </Typography>
              )}

              {/* Indicador circular para porcentajes */}
              {isPercentage && percentageValue !== undefined && (
                <Box position="relative" display="inline-flex">
                  <CircularProgress
                    variant="determinate"
                    value={percentageValue}
                    size={40}
                    thickness={5}
                    sx={{
                      color: percentageValue >= 80 ? '#4caf50' : percentageValue >= 60 ? '#ff9800' : '#f44336',
                    }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="caption" component="div" color="text.secondary" fontWeight="bold">
                      {`${Math.round(percentageValue)}`}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Trend text */}
            {trend && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, fontSize: '0.875rem' }}>
                {trend}
              </Typography>
            )}

            {/* Mini sparkline */}
            {sparklineData && sparklineData.length > 0 && (
              <Box sx={{ mt: 2, height: 40, width: '100%', minWidth: 100 }}>
                <ResponsiveContainer width="100%" height={40}>
                  <LineChart data={sparklineData.map((val, idx) => ({ value: val, index: idx }))}>
                    <Line
                      type="monotone"
                      dataKey="value"
                      stroke={color}
                      strokeWidth={2}
                      dot={false}
                      animationDuration={1000}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            )}
          </Box>

          {/* Icono con avatar */}
          <Avatar
            sx={{
              bgcolor: color,
              width: 56,
              height: 56,
              boxShadow: 3,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EnhancedStatCard;
